import { useCallback, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useBreakpoint } from '../hooks/useMediaQuery';
import { stashFiles } from '../hooks/pendingFiles';
import { useToast } from '../hooks/useToast';
import { DropOverlay } from '../components/DropOverlay';
import { ErrorBoundary } from '../components/ErrorMessage';
import { OpenAddMenuContext } from './addMenu';
import { AddSheet } from './AddSheet';
import { BottomTabs } from './BottomTabs';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';
import './shell.css';

/** The bank wizard owns every drop on its own page (spec section 9). */
const BANK_IMPORT_PATH = '/lagg-till/kontoutdrag';

const NOT_A_RECEIPT = 'Vi kan bara läsa bilder och PDF här. Ett kontoutdrag läser du in i guiden.';

/**
 * Grid `[nav] [main]`: SideNav ≥ 1024, icon rail 768–1023, TopBar + BottomTabs
 * below. Hosts the one Lägg till sheet and the whole-app drop target.
 */
export function AppShell() {
  const bp = useBreakpoint();
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toast = useToast();
  const openAdd = useCallback(() => setAddOpen(true), []);
  const closeAdd = useCallback(() => setAddOpen(false), []);

  const onDropFiles = useCallback(
    (files: File[]) => {
      stashFiles(files);
      navigate('/lagg-till/kvitto');
    },
    [navigate],
  );

  // The overlay invites any file; anything it cannot file must be named, not
  // swallowed — a dropped bank statement used to disappear without a word.
  const onRejectedFiles = useCallback(() => {
    toast.show(NOT_A_RECEIPT, {
      tone: 'warning',
      key: 'drop-rejected',
      action: { label: 'Läs in ett kontoutdrag', onClick: () => navigate(BANK_IMPORT_PATH) },
    });
  }, [navigate, toast]);

  return (
    <OpenAddMenuContext.Provider value={openAdd}>
      <div className={`app-shell app-shell-${bp}`}>
        <a href="#main" className="skip-link">
          Hoppa till innehållet
        </a>
        {bp === 'mobile' ? <TopBar /> : <SideNav rail={bp === 'tablet'} onAdd={openAdd} />}
        {bp === 'tablet' ? <TopBar /> : null}
        <main id="main" className="app-main" tabIndex={-1}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
        {bp === 'mobile' ? <BottomTabs onAdd={openAdd} /> : null}
        <AddSheet open={addOpen} onClose={closeAdd} />
        {/* Disabled on the import wizard: a PDF dropped beside its own zone
            used to hijack the import and file the statement as a receipt. */}
        <DropOverlay onFiles={onDropFiles} onRejected={onRejectedFiles} disabled={pathname.startsWith(BANK_IMPORT_PATH)} />
      </div>
    </OpenAddMenuContext.Provider>
  );
}
